import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DemoPlayerService } from '../demo-player.service';
import { DemoModeService } from '../demo-mode.service';
import { DEMO_FLOW_LABEL } from '../demo.constants';

/**
 * Renders the scripted-demo chrome on top of the real app: a gliding virtual
 * cursor, a click ripple, a highlight ring around the active target, and a
 * small control bar (restart / hide / exit) that can be hidden for a clean
 * recording. Purely presentational — all motion comes from
 * {@link DemoPlayerService} signals. Mounted once in the shell and shown only
 * while demo mode is active.
 */
@Component({
  selector: 'app-demo-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="demo-overlay">
      @if (intro(); as card) {
        <div class="demo-intro">
          <div class="demo-intro__grid" aria-hidden="true"></div>
          <div class="demo-intro__ghost" aria-hidden="true">{{ card.step }}</div>

          <div class="demo-intro__content">
            <div class="demo-intro__brand">
              <img src="/assets/icons/jestiyon.svg" alt="" class="demo-intro__logo" />
              <span>JestyLine</span>
            </div>

            <div class="demo-intro__kicker">
              <span class="demo-intro__tick"></span>
              Eğitim Serisi · {{ card.step }} / 03
              <span class="demo-intro__tick"></span>
            </div>

            <h1 class="demo-intro__title">{{ card.title }}</h1>
            <p class="demo-intro__subtitle">{{ card.subtitle }}</p>
            <span class="demo-intro__rule"></span>
          </div>

          <div class="demo-intro__progress" aria-hidden="true"><span></span></div>
        </div>
      }

      @if (highlight(); as rect) {
        <div
          class="demo-spotlight"
          [style.opacity]="spotOpacity()"
          [style.transform]="spotTransform()"
          [style.left.px]="rect.left - 8"
          [style.top.px]="rect.top - 8"
          [style.width.px]="rect.width + 16"
          [style.height.px]="rect.height + 16"
        ></div>
      }

      @if (tooltip(); as tip) {
        <div
          class="demo-tip"
          [class.demo-tip--above]="tip.above"
          [style.left.px]="tip.x"
          [style.top.px]="tip.y"
        >
          {{ caption() }}
        </div>
      }

      @if (cursorVisible()) {
        <div class="demo-cursor" [style.transform]="cursorTransform()">
          @if (clicking()) {
            <span class="demo-click-ring"></span>
          }
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 3.5 L5 19 L9 15 L11.5 20.5 L14 19.3 L11.6 14 L17 14 Z"
              fill="#ffffff"
              stroke="#1f2937"
              stroke-width="1.4"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      }

      @if (!controlsHidden()) {
        <div class="demo-controls">
          <span class="demo-badge">
            <span class="demo-dot" [class.demo-dot--done]="status() === 'done'"></span>
            {{ flowLabel() }} · {{ status() === 'done' ? 'Tamamlandı' : 'Oynatılıyor' }}
          </span>
          <button type="button" class="demo-btn" (click)="player.restart()">↻ Tekrar Oynat</button>
          <button type="button" class="demo-btn" (click)="player.toggleControls()">Gizle</button>
          <button type="button" class="demo-btn demo-btn--exit" (click)="player.exit()">Çıkış</button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .demo-overlay {
        position: fixed;
        inset: 0;
        z-index: 100000;
        pointer-events: none;
      }

      /* ── Title card: editorial fintech cover (light, on-brand) ───────── */
      .demo-intro {
        position: fixed;
        inset: 0;
        display: grid;
        place-items: center;
        overflow: hidden;
        background:
          radial-gradient(58% 48% at 50% 30%, rgba(55, 115, 254, 0.16), transparent 70%),
          linear-gradient(180deg, #ffffff 0%, #f5f6fa 100%);
        animation: demo-intro-fade 360ms ease-out;
      }

      /* faint brand dot-grid, faded toward the edges */
      .demo-intro__grid {
        position: absolute;
        inset: 0;
        background-image: radial-gradient(circle at center, rgba(55, 115, 254, 0.10) 1px, transparent 1.6px);
        background-size: 26px 26px;
        -webkit-mask-image: radial-gradient(120% 85% at 50% 32%, #000 30%, transparent 74%);
        mask-image: radial-gradient(120% 85% at 50% 32%, #000 30%, transparent 74%);
        opacity: 0;
        animation: demo-intro-fade 900ms ease-out 80ms forwards;
      }

      /* oversized ghost step number for editorial depth */
      .demo-intro__ghost {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -52%);
        font-size: clamp(240px, 36vw, 480px);
        font-weight: 800;
        line-height: 1;
        letter-spacing: -0.05em;
        color: rgba(4, 23, 71, 0.045);
        user-select: none;
        animation: demo-ghost-in 1000ms cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      .demo-intro__content {
        position: relative;
        z-index: 1;
        max-width: 760px;
        padding: 0 32px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .demo-intro__brand {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        padding: 8px 16px 8px 12px;
        background: #ffffff;
        border: 1px solid #ECEEF2;
        border-radius: 999px;
        box-shadow: 0 1px 3px rgba(4, 23, 71, 0.06), 0 8px 24px rgba(55, 115, 254, 0.08);
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.01em;
        color: #041747;
        margin-bottom: 28px;
        animation: demo-rise 620ms cubic-bezier(0.22, 1, 0.36, 1) 60ms both;
      }

      .demo-intro__logo { width: 20px; height: 20px; }

      .demo-intro__kicker {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: #3773fe;
        margin-bottom: 20px;
        animation: demo-rise 620ms cubic-bezier(0.22, 1, 0.36, 1) 150ms both;
      }

      .demo-intro__tick {
        width: 26px;
        height: 1.5px;
        border-radius: 2px;
        background: linear-gradient(90deg, transparent, rgba(55, 115, 254, 0.7));
      }
      .demo-intro__tick:last-child {
        background: linear-gradient(90deg, rgba(55, 115, 254, 0.7), transparent);
      }

      .demo-intro__title {
        margin: 0 0 16px;
        font-size: clamp(34px, 5vw, 54px);
        font-weight: 800;
        line-height: 1.06;
        letter-spacing: -0.03em;
        color: #041747;
        text-wrap: balance;
        animation: demo-rise 680ms cubic-bezier(0.22, 1, 0.36, 1) 250ms both;
      }

      .demo-intro__subtitle {
        margin: 0;
        max-width: 460px;
        font-size: 18px;
        font-weight: 500;
        line-height: 1.5;
        color: #475569;
        animation: demo-rise 680ms cubic-bezier(0.22, 1, 0.36, 1) 350ms both;
      }

      .demo-intro__rule {
        width: 64px;
        height: 3px;
        margin-top: 30px;
        border-radius: 99px;
        background: linear-gradient(90deg, #3773fe, #8eb0ff);
        transform-origin: center;
        animation: demo-rule 620ms cubic-bezier(0.22, 1, 0.36, 1) 470ms both;
      }

      .demo-intro__progress {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 3px;
        background: rgba(55, 115, 254, 0.12);
      }
      .demo-intro__progress span {
        display: block;
        height: 100%;
        width: 0;
        background: linear-gradient(90deg, #3773fe, #5c8bff);
        animation: demo-progress 2350ms linear 120ms forwards;
      }

      @keyframes demo-intro-fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes demo-rise {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes demo-ghost-in {
        from { opacity: 0; transform: translate(-50%, -52%) scale(1.07); }
        to { opacity: 1; transform: translate(-50%, -52%) scale(1); }
      }
      @keyframes demo-rule {
        from { opacity: 0; transform: scaleX(0); }
        to { opacity: 1; transform: scaleX(1); }
      }
      @keyframes demo-progress {
        from { width: 0; }
        to { width: 100%; }
      }

      .demo-cursor {
        position: fixed;
        top: 0;
        left: 0;
        width: 26px;
        height: 26px;
        transition: transform 650ms cubic-bezier(0.22, 1, 0.36, 1);
        filter: drop-shadow(0 2px 4px rgba(15, 23, 42, 0.35));
        will-change: transform;
      }

      .demo-click-ring {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 12px;
        height: 12px;
        border-radius: 9999px;
        background: rgba(55, 115, 254, 0.45);
        transform: translate(-50%, -50%) scale(1);
        animation: demo-ripple 520ms ease-out forwards;
      }

      @keyframes demo-ripple {
        0% { transform: translate(-50%, -50%) scale(1); opacity: 0.9; }
        100% { transform: translate(-50%, -50%) scale(5.5); opacity: 0; }
      }

      /* The spotlight: a transparent rounded window onto the target, with a
         huge box-shadow that dims the entire rest of the screen — cinematic
         focus without scaling/clipping the page. The CSS transition makes it
         glide between targets; the player pins it to the live rect each frame. */
      .demo-spotlight {
        position: fixed;
        border-radius: 12px;
        transform-origin: 50% 50%;
        box-shadow:
          0 0 0 2px rgba(255, 255, 255, 0.9),
          0 0 0 5px rgba(55, 115, 254, 0.85),
          0 0 0 9999px rgba(2, 6, 23, 0.68);
        /* The border carries the SAME transform as the page <main>, so the two
           animate as one composited motion — glued and smooth. Keep this
           duration/easing identical to ZOOM_MS in the player. */
        transition:
          transform 430ms cubic-bezier(0.22, 1, 0.36, 1),
          opacity 240ms ease;
        pointer-events: none;
        will-change: transform, opacity;
      }

      .demo-tip {
        position: fixed;
        transform: translate(-50%, 0);
        width: max-content;
        max-width: 380px;
        padding: 9px 14px;
        border-radius: 12px;
        background: #1f2937;
        color: #f9fafb;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        text-align: center;
        white-space: normal;
        box-shadow: 0 10px 28px rgba(15, 23, 42, 0.32);
        pointer-events: none;
        animation: demo-tip-in 200ms ease-out;
      }

      .demo-tip--above {
        transform: translate(-50%, -100%);
      }

      .demo-tip::after {
        content: '';
        position: absolute;
        left: 50%;
        width: 10px;
        height: 10px;
        background: #1f2937;
        transform: translateX(-50%) rotate(45deg);
      }

      .demo-tip:not(.demo-tip--above)::after { top: -5px; }
      .demo-tip--above::after { bottom: -5px; }

      @keyframes demo-tip-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .demo-controls {
        position: fixed;
        right: 18px;
        bottom: 18px;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 9999px;
        background: rgba(17, 24, 39, 0.92);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.28);
        pointer-events: auto;
        backdrop-filter: blur(6px);
      }

      .demo-badge {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: #e5e7eb;
        font-size: 12px;
        font-weight: 600;
        padding-left: 6px;
      }

      .demo-dot {
        width: 8px;
        height: 8px;
        border-radius: 9999px;
        background: #3773fe;
        animation: demo-pulse 1.2s ease-in-out infinite;
      }

      .demo-dot--done {
        background: #22c55e;
        animation: none;
      }

      @keyframes demo-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.35; }
      }

      .demo-btn {
        appearance: none;
        border: none;
        cursor: pointer;
        font-size: 12px;
        font-weight: 600;
        color: #f9fafb;
        background: rgba(255, 255, 255, 0.12);
        padding: 6px 12px;
        border-radius: 9999px;
        transition: background 150ms ease;
      }

      .demo-btn:hover { background: rgba(255, 255, 255, 0.22); }
      .demo-btn--exit { background: rgba(239, 68, 68, 0.85); }
      .demo-btn--exit:hover { background: rgba(239, 68, 68, 1); }
    `,
  ],
})
export class DemoOverlayComponent {
  protected readonly player = inject(DemoPlayerService);
  private readonly demoMode = inject(DemoModeService);

  protected readonly cursorVisible = this.player.cursorVisible;
  protected readonly clicking = this.player.clicking;
  protected readonly intro = this.player.intro;
  protected readonly highlight = this.player.highlight;
  protected readonly spotTransform = this.player.spotTransform;
  protected readonly spotOpacity = this.player.spotOpacity;
  protected readonly caption = this.player.caption;
  protected readonly captionAnchor = this.player.captionAnchor;
  protected readonly status = this.player.status;
  protected readonly controlsHidden = this.player.controlsHidden;

  protected readonly cursorTransform = computed(() => {
    const { x, y } = this.player.cursor();
    return `translate(${x}px, ${y}px)`;
  });

  /** Where to place the Turkish caption bubble: above the highlighted target if
   * there's room, otherwise below it; falls back to under the cursor. */
  protected readonly tooltip = computed<{ x: number; y: number; above: boolean } | null>(() => {
    const rect = this.captionAnchor();
    if (!this.caption() || !rect) return null;
    const above = rect.top > 110;
    const raw = {
      x: rect.left + rect.width / 2,
      y: above ? rect.top - 14 : rect.bottom + 14,
      above,
    };
    // Clamp so the centered bubble never runs off-screen (half of max-width).
    const margin = 200;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return { ...raw, x: Math.max(margin, Math.min(vw - margin, raw.x)) };
  });

  protected readonly flowLabel = computed(() => {
    const flow = this.demoMode.flow();
    return flow ? DEMO_FLOW_LABEL[flow] : 'Demo';
  });
}
